
export enum ERPModule {
  DASHBOARD = 'DASHBOARD',
  INFRASTRUCTURE = 'INFRASTRUCTURE',
  ORDERS_PIPELINE = 'ORDERS_PIPELINE',
  PORT_BIT_TRACKER = 'PORT_BIT_TRACKER',
  CRM = 'CRM',
  QUOTES = 'QUOTES',
  TASKS = 'TASKS',
  USERS = 'USERS'
}

export enum Country {
  TANZANIA = 'TANZANIA',
  UGANDA = 'UGANDA',
  KENYA = 'KENYA',

}

// Added comment above fix: Expanded UserRole to include roles found in the initial datasets and components to ensure type compatibility
export type UserRole = 'Admin' | 'Add/Edit' | 'Read Only' | 'Director' | 'Operations' | 'Sales';
export type UserStatus = 'active' | 'disabled';

export interface User {
  id: string;
  username: string;
  password?: string;
  fullName: string;
  designation?: string;
  role: UserRole;
  status: UserStatus;
  allowedModules: ERPModule[];
  homeCountry: Country;
  allowedCountries: Country[]; // Added to support multi-branch access
  avatarUrl?: string;
}

export interface LoginLog {
  id: string;
  username: string;
  fullName: string;
  ipAddress: string;
  loginTime: string;
}

export interface AppNotification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'alert' | 'deadline' | 'status';
  title: string;
  message: string;
  date: string;
  read: boolean;
}

// Infrastructure / Project Types
export enum ProjectStatus {
  PLANNING = 'Planning',
  UNDER_TENDERING = 'Under Tendering',
  IN_PROGRESS = 'In Progress',
  PAUSED = 'Paused',
  COMPLETED = 'Completed'
}

export interface ProjectUpdate {
  id: string;
  date: string;
  content: string;
  author: string;
}

export interface BitumenSpec {
  id: string;
  grade: string;
  quantity: number;
}

export interface Project {
  id: string;
  name: string;
  contractor: string;
  region: string;
  country: Country;
  kilometers: number;
  financer: string;
  consultant: string;
  status: ProjectStatus;
  progress: number;
  startDate: string;
  estimatedCompletion: string;
  bitumenSpecs: BitumenSpec[];
  updates: ProjectUpdate[];
  tasks: Task[];
  tenderNumber?: string;
}

// Finance / Quote Types
export interface BusinessInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  logoUrl?: string;
  tin: string;
  vrn: string;
  bankDetails: string;
}

export interface ClientInfo {
  name: string;
  email: string;
  phone: string;
  address: string;
  tin?: string;
  vrn?: string;
}

export interface QuoteDetails {
  number: string;
  date: string;
  priceValidity: string;
  currency: string;
  taxRate: number;
  amountInWords: string;
  paymentTerms: string;
  deliveryTerms: string;
  incoterms: string;
  notes: string;
}

export interface LineItem {
  id: string;
  name: string;
  description: string;
  quantity: number;
  price: number;
  unit: string;
  drumWeight?: number;
}

export interface QuoteState {
  business: BusinessInfo;
  client: ClientInfo;
  details: QuoteDetails;
  items: LineItem[];
}

export type TaskStatus = 'Pending' | 'In Progress' | 'Review' | 'Completed';

export interface Task {
  id: string;
  title: string;
  description?: string;
  dueDate: string;
  priority: 'High' | 'Medium' | 'Low';
  status: TaskStatus;
  assigneeId?: string;
  projectId?: string;
  contactId?: string;
  country: Country;
}

export interface Contact {
  id: string;
  companyName: string;
  companyAddress: string;
  poBox: string;
  tin: string;
  vrn: string;
  email: string;
  contactPerson: string;
  phone: string;
  role: string;
  lastContacted: string;
  notes: string;
  avatarUrl: string;
  sentiment: string;
  country: Country;
  region?: string;
}

// AI and Grounding Types
export interface GroundingSource {
  title: string;
  uri: string;
}

export interface ERPMessage {
  id: string;
  role: 'user' | 'model';
  content: string;
  type: 'text' | 'grounding';
  groundingSources?: GroundingSource[];
}

export enum Company {
  SHIV = 'SHIV INTERNATIONAL (UGANDA)',
  KAILASA = 'KAILASA INTERNATIONAL (KENYA)',
  SHIV_TZ = 'SHIV INTERNATIONAL (TANZANIA)'
}

export enum OrderStatus {
  PLACED = 'Placed',
  UNDER_PRODUCTION = 'Under Production',
  SHIPPED = 'Shipped',
  UNDER_CLEARANCE = 'Under Clearance',
  CLEARED = 'Cleared',
  CANCELLED = 'Cancelled'
}

export enum PaymentStatus {
  PENDING = 'Pending',
  PARTIAL = 'Partial',
  PAID = 'Paid'
}

export interface PaymentRecord {
  id: string;
  date: string;
  amount: number;
  type: 'Advance' | 'Part Payment' | 'Balance';
  note?: string;
}

export interface OrderDocument {
  id: string;
  name: string;
  type: string;
  date: string;
  size: string;
  url: string;
}

export const DEFAULT_CURRENCIES = ['USD', 'TZS', 'UGX', 'KES'];

export interface AISummary {
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  summary: string;
  recommendations: string[];
}

export interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
}

export enum BitumenGrade {
  G_60_70 = '60/70',
  G_80_100 = '80/100',
  G_40_50 = '40/50',
  G_50_70 = '50/70',
  MC_30 = 'MC30',
  MC_70 = 'MC70',
  K1_60 = 'K1-60',
  K1_70 = 'K1-70',
  C55 = 'C55',
  PG_70_10 = 'PG 70-10',
  PG_70_22 = 'PG 70-22',
  PG_70_16 = 'PG 70-16',
  PG_76_16 = 'PG 76-16',
  PG_76_10 = 'PG 76-10',
  UNKNOWN = 'UNKNOWN'
}

export interface ClearingInfo {
  agentName: string;
  documentsSent: boolean;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';
  costs: { id: string; name: string; amount: number; status: 'PENDING' | 'PAID' }[];
}

export interface MarineTrafficData {
  vesselName: string;
  imo: string;
  latitude: number;
  longitude: number;
  speed: number;
  course: number;
  status: string;
  lastUpdated: string;
  nextPort: string;
  eta: string;
}

export interface Order {
  id: string;
  orderDate: string;
  customerName: string;
  consigneeEmail?: string;
  consigneePhone?: string;
  consigneeAddress?: string;
  supplier: string;
  supplierEmail?: string;
  supplierPhone?: string;
  supplierAddress?: string;
  shipper?: string;
  shipperEmail?: string;
  shipperPhone?: string;
  shipperAddress?: string;
  portOfDestination: string;
  vesselPlanned?: string;
  ets?: string;
  eta?: string;
  blNumber?: string;
  company: Company;
  items: OrderItem[];
  financials: {
    totalAmount: number;
    currency: string;
    amountPaid: number;
    balanceAmount: number;
    paymentStatus: PaymentStatus;
    paymentHistory: PaymentRecord[];
  };
  currentStatus: OrderStatus;
  timeline: { status: OrderStatus; date: string; note: string }[];
  documents: OrderDocument[];
  notes?: string;
  clearingInfo?: ClearingInfo;
  trackingData?: MarineTrafficData;
}

export interface ResearchResult {
  summary: string;
  sources: GroundingSource[];
}

export enum DealStage {
  PROSPECTING = 'Prospecting',
  QUALIFICATION = 'Qualification',
  PROPOSAL = 'Proposal',
  NEGOTIATION = 'Negotiation',
  CLOSED_WON = 'Closed Won',
  CLOSED_LOST = 'Closed Lost'
}

export interface Deal {
  id: string;
  contactId: string;
  title: string;
  value: number;
  stage: DealStage;
  expectedCloseDate: string;
}

export interface Activity {
  id: string;
  contactId: string;
  type: 'Call' | 'Email' | 'Meeting' | 'Note' | 'WhatsApp';
  date: string;
  description: string;
}

export type AccessLevel = 'Admin' | 'User';

export interface Employee {
  id: string;
  name: string;
  role: string;
  accessLevel: AccessLevel;
  avatarUrl: string;
}

export enum PortName {
  DAR_ES_SALAAM = 'DAR ES SALAAM',
  MOMBASA = 'MOMBASA',
  TANGA = 'TANGA',
  ZANZIBAR = 'ZANZIBAR'
}

export interface ImportRecord {
  id: string;
  vesselName: string;
  importerName: string;
  importerAddress?: string;
  notifyParty?: string;
  supplier: string;
  supplierAddress?: string;
  finalDestination: string;
  bitumenGrade: string;
  quantity: number;
  arrivalDate: string;
  blNumber?: string;
  notes?: string;
}

export interface Vessel {
  id: string;
  name: string;
  imo: string;
  arrivalDate: string;
  status: 'Expected' | 'At Anchor' | 'Discharging' | 'Departed';
  port?: PortName;
  manifest: ImportRecord[];
}

export interface NameVariationCandidate {
  index: number;
  field: 'importerName' | 'supplier';
  currentValue: string;
  suggestedValue: string;
}
